import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { apiService } from '../services/apiService'

const CustomerAuthContext = createContext()

const IDLE_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes
const VISIBILITY_GRACE_MS = 30 * 1000 // 30 seconds grace when tab hidden

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext)
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider')
  }
  return context
}

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  
  const idleTimerRef = useRef(null)
  const visibilityTimerRef = useRef(null)
  const isAuthenticatedRef = useRef(false)

  // Keep ref in sync with state
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])

  useEffect(() => {
    checkAuthStatus()
  }, [])

  // Idle timeout & beforeunload setup
  useEffect(() => {
    if (!isAuthenticated) return

    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => {
        console.warn('Customer session timed out due to inactivity')
        logout()
      }, IDLE_TIMEOUT_MS)
    }

    const handleActivity = () => resetIdleTimer()
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    activityEvents.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }))
    resetIdleTimer()

    // beforeunload — fire-and-forget server logout
    const handleBeforeUnload = () => {
      if (isAuthenticatedRef.current) {
        apiService.beaconLogout(false, false)
        sessionStorage.removeItem('customerToken')
        sessionStorage.removeItem('customerData')
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    // visibilitychange — grace period when tab hidden
    const handleVisibility = () => {
      if (document.hidden) {
        visibilityTimerRef.current = setTimeout(() => {
          console.warn('Customer session ended — tab hidden too long')
          logout()
        }, VISIBILITY_GRACE_MS)
      } else {
        if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current)
        resetIdleTimer()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      activityEvents.forEach(evt => window.removeEventListener(evt, handleActivity))
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibility)
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current)
    }
  }, [isAuthenticated])

  const checkAuthStatus = async () => {
    try {
      const token = sessionStorage.getItem('customerToken')
      const customerData = sessionStorage.getItem('customerData')
      
      if (token && customerData) {
        setCustomer(JSON.parse(customerData))
        setIsAuthenticated(true)
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      logout()
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (memberNo, pin) => {
    try {
      setIsLoading(true)
      const response = await apiService.memberLogin(memberNo, pin)

      if (response.status === 'SUCCESS') {
        sessionStorage.setItem('customerToken', response.data.sessionAuthToken)
        sessionStorage.setItem('customerData', JSON.stringify(response.data.user))
        setCustomer(response.data.user)
        setIsAuthenticated(true)
        return { success: true }
      } else {
        return { success: false, error: response.message }
      }
    } catch (error) {
      console.error('Login failed:', error)
      return { success: false, error: error.message || 'Login failed. Please try again.' }
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (registrationData) => {
    try {
      setIsLoading(true)
      const response = await apiService.customerRegister(registrationData)
      
      if (response.success) {
        return { success: true, message: response.message }
      } else {
        return { success: false, error: response.message }
      }
    } catch (error) {
      console.error('Registration failed:', error)
      return { success: false, error: 'Registration failed. Please try again.' }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    // Server-side session invalidation (fire-and-forget)
    apiService.serverLogout(false, false).catch(() => {})
    sessionStorage.removeItem('customerToken')
    sessionStorage.removeItem('customerData')
    setCustomer(null)
    setIsAuthenticated(false)
  }

  const value = {
    customer,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout
  }

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  )
}
